import { useCallback, useRef } from 'react'

import debounce from 'lodash/debounce'
import type { ListOnScrollProps } from 'react-window'

import { useSizeObserver } from '@/modules/chatting/ui/sidebar/use-size-observer'

interface Options<Entity extends object> {
  canLoadMore: boolean
  onLoadMore: () => void
  entities: Entity[]
}

export const useInfiniteScroll = <Entity extends object>({
  canLoadMore,
  onLoadMore,
  entities,
}: Options<Entity>) => {
  const lastVisibleIndexRef = useRef<number | null>(null)
  const [parentRef, width, height] = useSizeObserver()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onScroll = useCallback(
    debounce(({ scrollDirection }: ListOnScrollProps) => {
      const lastItemIndex = lastVisibleIndexRef.current

      if (
        lastItemIndex === null ||
        lastItemIndex !== entities.length - 1 ||
        !canLoadMore
      ) {
        return
      }

      if (scrollDirection === 'forward') {
        onLoadMore()
      }
    }, 100),
    [onLoadMore, canLoadMore, entities.length],
  )

  return {
    onScroll,
    lastVisibleIndexRef,
    container: {
      ref: parentRef,
      width,
      height,
    },
  }
}
